import type { AppliedSuggestion } from '@app/src/pages/Questionnaire/Provider/Report/SuggestedAnswers/suggestionSources'
import type { AnswerStatus, RequestItem } from '@app/src/types/resourceExplorer'
import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import ResponseItemContext from './ResponseItemContext'
import { useProviderReport } from '../pages/Questionnaire/Provider/ProviderReportContext'

export enum ResponseItemView {
  ShowSuggestedAnswer = 'ShowSuggestedAnswer',
  SuggestedAnswerAccepted = 'SuggestedAnswerAccepted',
  SkipQuestion = 'SkipQuestion',
}

interface Props {
  children: JSX.Element
  requestItem: RequestItem
  cannotAnswer: boolean
  sectionNumber: number
  itemNumber: number
}

const ResponseItemContextProvider = ({
  children,
  requestItem,
  cannotAnswer,
  sectionNumber,
  itemNumber,
}: Props): JSX.Element => {
  const [responseItemView, setResponseItemView] = useState<ResponseItemView>(ResponseItemView.ShowSuggestedAnswer)
  const [appliedSuggestion, setAppliedSuggestion] = useState<AppliedSuggestion>()
  const { suggestedAnswersPerQuestion } = useProviderReport()
  const { setValue } = useFormContext()

  const previousAnswer = suggestedAnswersPerQuestion?.find(
    item => item.requestItem.questionId === requestItem.questionId,
  )

  const setQuestionSkippedFields = (answerStatus?: AnswerStatus) => {
    if (answerStatus) {
      setValue(`${requestItem.id}.cannotAnswer`, true)
      setValue(`${requestItem.id}.planOnHaving`, false)
      setValue(`${requestItem.id}.estimatedAnswerAt`, undefined)
      setValue(`${requestItem.id}.answerStatus`, answerStatus)
      setValue(`${requestItem.id}.comment`, '')
      return
    }

    if (!previousAnswer) return
    setValue(`${requestItem.id}.cannotAnswer`, true)
    setValue(`${requestItem.id}.planOnHaving`, Boolean(previousAnswer.estimatedAnswerAt))
    setValue(`${requestItem.id}.estimatedAnswerAt`, previousAnswer.estimatedAnswerAt)
    setValue(`${requestItem.id}.answerStatus`, previousAnswer.answerStatus)
    setValue(`${requestItem.id}.comment`, previousAnswer.comment)
  }

  return (
    <ResponseItemContext.Provider
      value={{
        responseItemView: responseItemView,
        setResponseItemView: setResponseItemView,
        previousAnswer,
        /* Editing or removing the answer moves the view off SuggestedAnswerAccepted, which is what
           brings the suggestion button back in place of the applied-answer note. */
        appliedSuggestion:
          responseItemView === ResponseItemView.SuggestedAnswerAccepted ? appliedSuggestion : undefined,
        setAppliedSuggestion,
        hideInputField: cannotAnswer,
        setQuestionSkippedFields,
        sectionNumber: sectionNumber,
        itemNumber: itemNumber,
      }}
    >
      {children}
    </ResponseItemContext.Provider>
  )
}

export default ResponseItemContextProvider
